import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserPlus, KeyRound } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volunteer-dispatch-6/artifacts/8p87dbov_hope-to-home-350x66.png";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", invite_code: "", member_group: "board",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Please pick a password with at least 8 characters.");
      return;
    }
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome aboard, ${res.user.name.split(" ")[0]}!`);
      navigate("/");
    } else {
      toast.error(res.error || "Could not create your account.");
    }
  };

  return (
    <div className="min-h-screen surface-public grain flex flex-col items-center justify-center px-4 py-10">
      <Link to="/login" className="mb-8 focus-ring rounded-md" data-testid="register-logo-link">
        <img src={LOGO_URL} alt="Hope to Home" className="h-12 w-auto" />
      </Link>
      <div className="w-full max-w-md card-elevated rounded-xl p-7 sm:p-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Create your account</h1>
        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
          Board members & associates only. You'll need the invite code from the latest newsletter or board meeting.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label htmlFor="reg-name" className="text-sm font-semibold">Full name</Label>
            <Input
              id="reg-name"
              data-testid="register-name-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              autoComplete="name"
              required
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <div>
            <Label htmlFor="reg-email" className="text-sm font-semibold">Email</Label>
            <Input
              id="reg-email"
              data-testid="register-email-input"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              autoComplete="email"
              required
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <div>
            <Label htmlFor="reg-phone" className="text-sm font-semibold">Mobile phone</Label>
            <Input
              id="reg-phone"
              data-testid="register-phone-input"
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              autoComplete="tel"
              required
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold">I'm a…</Label>
            <Select value={form.member_group} onValueChange={(v) => update("member_group", v)}>
              <SelectTrigger data-testid="register-group-select" className="mt-1.5 min-h-[44px] text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="board" data-testid="group-option-board">Board Member</SelectItem>
                <SelectItem value="alternate" data-testid="group-option-alternate">Alternate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="reg-password" className="text-sm font-semibold">Password</Label>
            <Input
              id="reg-password"
              data-testid="register-password-input"
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className="mt-1.5 text-base min-h-[44px]"
            />
            <p className="text-xs text-slate-400 mt-1">8+ characters.</p>
          </div>
          <div>
            <Label htmlFor="reg-code" className="text-sm font-semibold flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5 text-brand-blue" /> Invite code
            </Label>
            <Input
              id="reg-code"
              data-testid="register-invite-input"
              value={form.invite_code}
              onChange={(e) => update("invite_code", e.target.value)}
              required
              className="mt-1.5 text-base min-h-[44px] uppercase tracking-wider"
              placeholder="HOPE2026"
            />
          </div>
          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={submitting}
            className="w-full min-h-[48px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {submitting ? "Creating account…" : "Create account"}
          </Button>
        </form>
        <p className="text-xs text-slate-500 mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-brand-blue font-semibold hover:underline" data-testid="register-to-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
